/**
 * The obligation's conversation — shared by the State Contracts register, the
 * Governance Hub cards, and (via the same endpoints) the owner's modal on
 * Workspace. Comments travel both directions until the item is delivered, and
 * every lifecycle event lands in between so the exchange reads in context.
 *
 * Lifted out of SmacRegister so the Hub's calendar and obligations tabs can
 * carry the same thread — the calendar card used to be a dead end: no way to
 * talk to the owner from the place that told you the work was due.
 */
import React, { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

export default function ThreadPanel({ obligationId }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const d = await fetch(`${API}/api/smac/obligations/${obligationId}/activity`, { credentials: 'include' })
      .then(r => r.json()).catch(() => null);
    if (d?.success) setEvents(d.events);
    else setError(d?.message || 'The thread could not be loaded.');
  }, [obligationId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const send = async () => {
    const text = comment.trim();
    if (!text) return;
    setBusy(true); setError('');
    const d = await fetch(`${API}/api/smac/obligations/${obligationId}/comments`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: text }),
    }).then(r => r.json()).catch(() => null);
    if (d?.success) { setComment(''); await load(); }
    else setError(d?.message || 'The comment could not be saved.');
    setBusy(false);
  };

  const LABEL = {
    comment: 'commented', flagged: 'raised a problem', completed: 'completed the item',
    attested: 'confirmed it is in place', activated: 'activated the item', retired: 'retired the item',
    assigned: 'assigned the item', closure_requested: 'asked for this to be closed',
    closure_declined: 'declined the closure request',
  };
  const comments = (events || []).filter(e => e.action === 'comment').length;

  return (
    <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen(v => !v)}
        title="The conversation on this item — you and the owner, until it is delivered"
        className="text-xs font-medium text-slate-500 hover:text-slate-700">
        {open ? 'Hide the thread' : `Thread${events ? ` (${comments})` : ''} — talk to the owner`}
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3.5 space-y-2.5">
          {events === null && !error && <p className="text-xs text-slate-400">Loading…</p>}
          {events !== null && events.length === 0 && <p className="text-xs text-slate-400">Nothing here yet.</p>}
          {(events || []).map(e => (
            <div key={e.id} className={e.action === 'comment' ? 'rounded-lg bg-slate-50 px-3 py-2' : 'px-1'}>
              <p className="text-[11px] text-slate-400">
                <span className="font-medium text-slate-600">{e.user_name || 'Someone'}</span>
                {' '}{LABEL[e.action] || e.action}
                {' · '}{new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
              {e.note && <p className="text-[12.5px] text-slate-700 mt-0.5 whitespace-pre-wrap">{e.note}</p>}
            </div>
          ))}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2 pt-1">
            <input value={comment} onChange={e => setComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              placeholder="Write to the owner…"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-orange-300" />
            <button type="button" disabled={busy || !comment.trim()} onClick={send}
              title="Add your comment — the owner sees it on their item"
              className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-40">
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
